import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/user/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка входа'
      };
    }
  };

  const sendEmailCode = async (email) => {
    try {
      await axios.post('/api/auth/send-email-code', { email });
      return { success: true };
    } catch (error) {
      const cooldown = error.response?.data?.cooldown;
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка отправки кода',
        cooldown
      };
    }
  };

  const verifyEmailCode = async (email, code) => {
    try {
      const response = await axios.post('/api/auth/verify-email-code', { email, code });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Неверный код'
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', {
        name,
        email,
        password
      });
      // Email verification required — not logged in yet
      if (response.data.requiresVerification) {
        return { success: true, requiresVerification: true };
      }
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка регистрации'
      };
    }
  };

  const setTokenAndUser = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const sendCode = async (phone) => {
    try {
      const response = await axios.post('/api/auth/send-code', { phone });
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Ошибка отправки кода';
      const errorType = error.response?.data?.error;
      const cooldown = error.response?.data?.cooldown;
      
      // Provide more user-friendly messages
      let userMessage = errorMessage;
      if (errorType === 'ACCOUNT_NOT_FOUND' || errorType === 'ACCOUNT_ERROR') {
        userMessage = 'Сервис временно недоступен. Попробуйте позже или обратитесь в поддержку.';
      }
      
      // If cooldown error, return cooldown info
      if (error.response?.status === 429 && cooldown) {
        return {
          success: false,
          message: userMessage,
          error: errorType,
          cooldown: cooldown
        };
      }
      
      return {
        success: false,
        message: userMessage,
        error: errorType
      };
    }
  };

  const verifyCode = async (phone, code, referralCode = null) => {
    try {
      const payload = { phone, code };
      if (referralCode) {
        payload.referralCode = referralCode;
      }
      const response = await axios.post('/api/auth/verify-code', payload);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка проверки кода'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, fetchUser, sendCode, verifyCode, setTokenAndUser, sendEmailCode, verifyEmailCode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

